<template>
  <div class="editor">
    <div class="toolbar">
      <ul class="nav">
        <li class="nav-item">
          <button @click="editor.chain().focus().toggleBold().run()"
                  :disabled="!editor.can().chain().focus().toggleBold().run()"
                  :class="{ 'is-active': editor.isActive('bold') }" class="btn"
          >
            <em class="align-middle material-icons">format_bold</em>
          </button>
        </li>
        <li class="nav-item">
          <button @click="editor.chain().focus().toggleItalic().run()"
                  :disabled="!editor.can().chain().focus().toggleItalic().run()"
                  :class="{ 'is-active': editor.isActive('italic') }" class="btn"
          >
            <em class="align-middle material-icons">format_italic</em>
          </button>
        </li>
        <li class="nav-item">
          <button @click="editor.chain().focus().toggleUnderline().run()"
                  :disabled="!editor.can().chain().focus().toggleUnderline().run()"
                  :class="{ 'is-active': editor.isActive('underline') }" class="btn"
          >
            <em class="align-middle material-icons">format_underline</em>
          </button>
        </li>
        <li class="nav-item">
          <button @click="editor.chain().focus().toggleStrike().run()"
                  :disabled="!editor.can().chain().focus().toggleStrike().run()"
                  :class="{ 'is-active': editor.isActive('strike') }" class="btn"
          >
            <em class="align-middle material-icons">format_strikethrough</em>
          </button>
        </li>
        <li class="nav-item dropdown">
          <a class="nav-link dropdown-toggle" data-bs-toggle="dropdown" href="#" role="button" aria-expanded="false">
            <em class="align-middle material-symbols-outlined">format_h1</em>
          </a>
          <ul class="dropdown-menu">
            <li class="dropdown-item">
              <button @click="editor.chain().focus().toggleHeading({level : 1}).run()"
                      :class="{ 'is-active': editor.isActive('heading', {level: 1}) }" class="btn"
              >
                <em class="align-middle material-symbols-outlined">format_h1</em>
              </button>
            </li>
            <li class="dropdown-item">
              <button @click="editor.chain().focus().toggleHeading({level : 2}).run()"
                      :class="{ 'is-active': editor.isActive('heading', {level: 2}) }" class="btn"
              >
                <em class="align-middle material-symbols-outlined">format_h2</em>
              </button>
            </li>
            <li class="dropdown-item">
              <button @click="editor.chain().focus().toggleHeading({level : 3}).run()"
                      :class="{ 'is-active': editor.isActive('heading', {level: 3}) }" class="btn"
              >
                <em class="align-middle material-symbols-outlined">format_h3</em>
              </button>
            </li>
            <li class="dropdown-item">
              <button @click="editor.chain().focus().toggleHeading({level : 4}).run()"
                      :class="{ 'is-active': editor.isActive('heading', {level: 4}) }" class="btn"
              >
                <em class="align-middle material-symbols-outlined">format_h4</em>
              </button>
            </li>
          </ul>
        </li>
        <li class="nav-item">
          <button @click="editor.chain().focus().toggleBulletList().run()"
                  :disabled="!editor.can().chain().focus().toggleBulletList().run()"
                  :class="{ 'is-active': editor.isActive('bulletList') }" class="btn"
          >
            <em class="align-middle material-icons">format_list_bulleted</em>
          </button>
        </li>
        <li class="nav-item">
          <button @click="editor.chain().focus().toggleOrderedList().run()"
                  :disabled="!editor.can().chain().focus().toggleOrderedList().run()"
                  :class="{ 'is-active': editor.isActive('orderedList') }" class="btn"
          >
            <em class="align-middle material-icons">format_list_numbered</em>
          </button>
        </li>
        <li class="nav-item">
          <div class="button-group">
            <button @click="setLink" :class="{ 'is-active': editor.isActive('link') }" class="btn">
              <em class="align-middle material-icons">insert_link</em>
            </button>
            <button @click="editor.chain().focus().unsetLink().run()"
                    :disabled="!editor.isActive('link')" class="btn">
              <em class="align-middle material-icons">link_off</em>
            </button>
          </div>
        </li>
        <li class="nav-item">
          <button @click="editor.chain().focus().toggleBlockquote().run()"
                  :disabled="!editor.can().chain().focus().toggleBlockquote().run()"
                  :class="{ 'is-active': editor.isActive('blockquote') }" class="btn"
          >
            <em class="align-middle material-icons">format_quote</em>
          </button>
        </li>
        <li class="nav-item">
          <button @click="editor.chain().focus().setHorizontalRule().run()" class="btn">
            <em class="align-middle material-icons">horizontal_rule</em>
          </button>
        </li>
        <li class="nav-item">
          <button @click="editor.chain().focus().undo().run()" :disabled="!editor.can().chain().focus().undo().run()" class="btn">
            <em class="align-middle material-icons">undo</em>
          </button>
        </li>
        <li class="nav-item">
          <button @click="editor.chain().focus().redo().run()" :disabled="!editor.can().chain().focus().redo().run()" class="btn">
            <em class="align-middle material-icons">redo</em>
          </button>
        </li>
      </ul>
    </div>
    <editor-content :editor="editor" class="editor-content" :id="this.id"></editor-content>
  </div>
</template>

<script>
import {Editor, EditorContent} from "@tiptap/vue-3";
import Document from '@tiptap/extension-document'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import Link from "@tiptap/extension-link";
import Bold from '@tiptap/extension-bold'
import Italic from '@tiptap/extension-italic'
import Strike from '@tiptap/extension-strike'
import Underline from '@tiptap/extension-underline'
import { ListKit } from '@tiptap/extension-list'
import Blockquote from '@tiptap/extension-blockquote'
import Heading from '@tiptap/extension-heading'
import HorizontalRule from '@tiptap/extension-horizontal-rule'
import { UndoRedo } from '@tiptap/extensions'

export default {
  components: {
    EditorContent
  },
  data: function () {
    return {
      editor: null,
    }
  },
  created() {
    this.editor = new Editor({
      extensions: [
        Link.configure({
          openOnClick: false,
          defaultProtocol: 'https',
        }),
        Document,
        Paragraph,
        Text,
        Bold,
        Italic,
        Underline,
        Strike,
        ListKit,
        Blockquote,
        Heading.configure({ levels: [1, 2, 3, 4] }),
        HorizontalRule,
        UndoRedo
      ],
      content: this.modelValue,
      autofocus: true,
      editable: true,
      onUpdate: () => {
        this.$emit('update:modelValue', this.editor.getHTML())
      },
    })
  },
  beforeUnmount() {
    this.editor.destroy();
  },
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    id: {
      type: String,
      default: '',
    }
  },
  emits: ['update:modelValue'],
  methods: {
    setLink() {
      const previousUrl = this.editor.getAttributes('link').href
      const url = window.prompt('URL', previousUrl)

      // cancelled
      if (url === null) {
        return
      }

      // empty
      if (url === '') {
        this.editor.chain().focus().extendMarkRange('link').unsetLink().run()

        return
      }

      // update link
      this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    },
  },
  watch: {
    modelValue(value) {
      const isSame = this.editor.getHTML() === value

      if (isSame) {
        return
      }

      this.editor.commands.setContent(value)
    },
  },
}
</script>

<style>
.editor {
  border: 1px solid #ccc;
  border-radius: 0.5rem;
}

.toolbar {
  border-bottom: 1px solid #ccc;
}

.editor-content div {
  padding: 1rem;
}

.is-active {
  background-color: lightgrey;
  color: #6229ff;
}
</style>